/**
 * 平台管理相关API接口
 */

import { unifiedApi } from '../../utils/unifiedApi';

// 租户类型枚举
export enum TenantType {
  DISTRIBUTOR = 1,  // 分销商
  SUPPLIER = 2,     // 供应商
  CARRIER = 3,      // 承运商
}

// 租户状态枚举
export enum TenantStatus {
  DISABLED = 0,  // 停用
  NORMAL = 1,    // 正常
}

// 租户接口
export interface Tenant {
  id: string;
  name: string;
  type: TenantType;
  typeName: string;
  company: string;
  organizationName?: string;
  uniformSocialCreditCode?: string;
  adminUserId?: string;
  adminUsername?: string;
  adminRealName?: string;
  status: TenantStatus;
  expTime?: string;
  maxUsers: number;
  currentUsers: number;
  maxStorage: number;
  usedStorage: number;
  features: string[];
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

// 租户查询参数
export interface TenantQueryParams {
  current?: number;
  pageSize?: number;
  name?: string;
  company?: string;
  type?: TenantType;
  status?: TenantStatus;
  adminUsername?: string;
  expTimeStart?: string;
  expTimeEnd?: string;
  createTimeStart?: string;
  createTimeEnd?: string;
}

// 租户表单数据
export interface TenantFormData {
  id?: string;
  name: string;
  type: TenantType;
  company: string;
  organizationName?: string;
  uniformSocialCreditCode?: string;
  adminUsername?: string;
  status: TenantStatus;
  expTime?: string;
  maxUsers: number;
  maxStorage: number;
  features: string[];
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  description?: string;
}

// 租户管理员绑定参数
export interface TenantAdminBindParams {
  tenantId: string;
  username: string;
  realName?: string;
  phone?: string;
  email?: string;
}

// 租户统计
export interface TenantStatistics {
  totalTenants: number;
  normalTenants: number;
  disabledTenants: number;
  distributorTenants: number;
  supplierTenants: number;
  carrierTenants: number;
  expiringSoonTenants: number;
  totalUsers: number;
  totalStorage: number;
  usedStorage: number;
}

// 租户配置
export interface TenantConfig {
  id: string;
  tenantId: string;
  configKey: string;
  configValue: string;
  configType: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// 部署类型枚举
export enum DeployType {
  SHARED = 'shared',       // 共享部署
  INDEPENDENT = 'independent', // 独立部署
}

// 健康状态枚举
export enum HealthStatus {
  HEALTHY = 'healthy',
  UNHEALTHY = 'unhealthy',
  UNKNOWN = 'unknown',
}

// API凭证
export interface APICredentials {
  api_key: string;
  api_secret: string;
}

// 部署信息
export interface DeployInfo {
  deploy_type: DeployType;
  db_schema?: string;
  db_host?: string;
  db_port?: number;
  db_name?: string;
  instance_url?: string;
  custom_domain?: string;
  ssl_cert_path?: string;
  health_status: HealthStatus;
  last_heartbeat_at?: string;
}

// 域名信息
export interface DomainInfo {
  tenant_code: string;
  custom_domain?: string;
  ssl_cert_path?: string;
  default_domain: string;
  domain_status: 'pending' | 'active' | 'error';
}

// 切换部署类型请求
export interface SwitchDeployTypeRequest {
  deploy_type: DeployType;
  db_schema?: string;
  db_host?: string;
  db_port?: number;
  db_name?: string;
  instance_url?: string;
  custom_domain?: string;
}

// 更新域名请求
export interface UpdateDomainRequest {
  custom_domain?: string;
  ssl_cert_path?: string;
}

/**
 * 获取租户列表
 */
export const getTenantList = async (params: TenantQueryParams = {}) => {
  return unifiedApi.service('platform').get('/tenant', { params });
};

/**
 * 获取租户详情
 */
export const getTenantById = async (id: string) => {
  return unifiedApi.service('platform').get(`/tenant/${id}`);
};

/**
 * 创建租户
 */
export const createTenant = async (data: TenantFormData) => {
  return unifiedApi.service('platform').post('/tenant', data);
};

/**
 * 更新租户
 */
export const updateTenant = async (id: string, data: Partial<TenantFormData>) => {
  return unifiedApi.service('platform').put(`/tenant/${id}`, data);
};

/**
 * 删除租户
 */
export const deleteTenant = async (id: string) => {
  return unifiedApi.service('platform').delete(`/tenant/${id}`);
};

/**
 * 更新租户状态
 */
export const updateTenantStatus = async (id: string, status: TenantStatus) => {
  return unifiedApi.service('platform').patch(`/tenant/${id}/status`, { status });
};

/**
 * 绑定租户管理员
 */
export const bindTenantAdmin = async (params: TenantAdminBindParams) => {
  return unifiedApi.service('platform').post('/tenant/bind-admin', params);
};

/**
 * 获取租户统计
 */
export const getTenantStatistics = async () => {
  return unifiedApi.service('platform').get('/tenant/statistics');
};

// ===== 平台运营数据分析（REQ-20260616-0005 P1 Dashboard）=====

/** 平台仪表盘统计（租户/用户/收入/订单 KPI） */
export interface PlatformDashboardStats {
  totalTenants: number; activeTenants: number; trialTenants: number;
  newTenantsToday: number; tenantGrowth: number;
  totalUsers: number; activeUsers: number; newUsersToday: number; userGrowth: number;
  totalRevenue: number; monthlyRevenue: number; revenueGrowth: number; currency: string;
  totalOrders: number; pendingOrders: number; todayOrders: number; conversionRate: number;
}
export const getPlatformDashboardStats = async () => {
  return unifiedApi.service('platform').get('/platform/analytics/dashboard');
};

/** 租户新增/流失趋势 */
export interface TenantTrendData { date: string; new_tenants: number; churn_tenants: number; active_total: number; trial_total: number; }
export const getPlatformTenantTrend = async (params: { start_date: string; end_date: string; group_by?: string }) => {
  return unifiedApi.service('platform').get('/platform/analytics/tenant-trend', { params });
};

/** GMV/收入构成（按套餐/业务） */
export const getPlatformRevenueBreakdown = async (params?: Record<string, unknown>) => {
  return unifiedApi.service('platform').get('/platform/analytics/revenue-breakdown', { params });
};

/** 租户 GMV 排行 */
export const getPlatformTopTenants = async (params?: { limit?: number }) => {
  return unifiedApi.service('platform').get('/platform/analytics/top-tenants', { params });
};

// ===== 租户层级树（REQ-20260616-0005 P2）=====

/** 租户树节点（/api/v1/tenants/tree 返回；request.ts 已 keysToCamel → 全 camelCase） */
export interface TenantTreeNode {
  id: number; parentId: number; name: string; code: string;
  type: string; status: string; statusLabel?: string;
  contactName?: string; industry?: string; parentName?: string;
  createdAt?: string;
  // P2.5 enrich：套餐 / 分销层级 / 下级计数 / 订阅营收 roll-up
  planCode?: string; planName?: string;
  distributionLevel?: number; isDistributor?: boolean;
  childrenCount?: number; descendantCount?: number;
  subscriptionRevenue?: number; revenueRollup?: number;
  children?: TenantTreeNode[];
}
/** 获取富化租户层级树（含套餐/订阅营收 roll-up，平台鉴权路由；root_id 为空=全部顶级及下级） */
export const getPlatformTenantTree = async (rootId?: number) => {
  return unifiedApi.service('platform').get('/platform/tenants/tree', { params: rootId ? { root_id: rootId } : {} });
};

// ===== 租户详情（REQ-20260616-0005 P3 抽屉）=====

export interface TenantQuotaItem {
  type: string; typeLabel?: string; usedAmount: number; maxAmount: number;
  remaining: number; usagePercent: number; isUnlimited: boolean; isExceeded: boolean; period?: string;
}
export interface PlatformTenantInfo {
  name?: string; code?: string; status?: string; industry?: string; scale?: string;
  contactName?: string; contactPhone?: string; contactEmail?: string; address?: string; remark?: string;
  planName?: string; subscriptionEnd?: string; autoRenew?: boolean;
}
export interface PlatformTenantDetail {
  tenant: PlatformTenantInfo;
  subscription?: { planName?: string; subscriptionEnd?: string };
  quotas?: TenantQuotaItem[];
  recentOrders?: Array<Record<string, unknown>>;
  distribution?: {
    level: number; isDistributor: boolean; parentId: number; parentName?: string;
    distributionPath?: string; childrenCount: number; descendantCount: number;
  };
  visibility?: { isPublic: boolean; visibleToTypes: string[]; visibleToIndustries: string[] };
}
export const getPlatformTenantDetail = async (id: number | string) => {
  return unifiedApi.service('platform').get<PlatformTenantDetail>(`/platform/tenants/${id}`);
};

// ===== 租户 CRUD + 生命周期（REQ-20260617-0003 P1）=====

export interface CreatePlatformTenantPayload {
  name: string; code: string; type?: string;
  contact_name: string; contact_phone: string; contact_email: string;
  industry?: string; scale?: string; address?: string;
  plan_id?: number; trial_days?: number; remark?: string;
  admin_username?: string; admin_password?: string;
}
export interface CreatePlatformTenantResult {
  tenant: PlatformTenantInfo;
  initial_username: string;
  initial_password: string;
}

/** 平台创建租户（自动 bootstrap 初始管理员，返回一次性初始凭据） */
export const createPlatformTenant = async (data: CreatePlatformTenantPayload) => {
  return unifiedApi.service('platform').post('/platform/tenants', data);
};
/** 平台编辑租户（基本信息，body 带 id） */
export const updatePlatformTenant = async (data: { id: number } & Record<string, unknown>) => {
  return unifiedApi.service('platform').put('/platform/tenants', data);
};
/** 变更租户套餐 */
export const changePlatformTenantPlan = async (data: { tenant_id: number; new_plan_id: number; duration_months?: number; reason?: string }) => {
  return unifiedApi.service('platform').put('/platform/tenants/plan', data);
};
/** 冻结租户（全员禁登录） */
export const freezePlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${id}/freeze`, {});
};
/** 解冻租户 */
export const unfreezePlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${id}/unfreeze`, {});
};
/** 注销租户（可逆） */
export const cancelPlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${id}/cancel`, {});
};
/** 取消注销（canceled→active） */
export const restorePlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${id}/restore`, {});
};
/** 回收站恢复（软删→恢复，恢复后为注销态） */
export const recoverPlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${id}/recover`, {});
};
/** 软删除租户（→回收站） */
export const softDeletePlatformTenant = async (id: number | string) => {
  return unifiedApi.service('platform').delete(`/platform/tenants/${id}`);
};
/** 回收站列表（已软删租户） */
export const getPlatformTenantRecycleBin = async (params?: { page?: number; page_size?: number; search?: string }) => {
  return unifiedApi.service('platform').get('/platform/tenants/recycle-bin', { params: params || {} });
};

// ===== 租户域名管理（REQ-20260617-0003 P2）=====

export interface DomainBindingInfo {
  id: number;
  tenantId: number;
  domain: string;
  scope: 'admin' | 'mall';
  bindingType: string;
  sslEnabled: boolean;
  createdAt: string;
  verifiedAt?: string;
}

/** 列出租户域名绑定 */
export const listTenantDomains = async (tenantId: number | string) => {
  return unifiedApi.service('platform').get<{ list: DomainBindingInfo[] }>(`/platform/tenants/${tenantId}/domains`);
};
/** 为租户绑定自定义域名（scope=admin|mall） */
export const bindTenantDomain = async (
  tenantId: number | string,
  data: { domain: string; scope: 'admin' | 'mall'; binding_type?: string },
) => {
  return unifiedApi.service('platform').post(`/platform/tenants/${tenantId}/domains`, {
    binding_type: 'custom',
    ...data,
  });
};
/** 解绑租户域名 */
export const unbindTenantDomain = async (tenantId: number | string, bindingId: number) => {
  return unifiedApi.service('platform').delete(`/platform/tenants/${tenantId}/domains/${bindingId}`);
};

// ===== 平台为租户账号重置密码 =====
export interface PlatformTenantUser {
  id: number;
  username: string;
  realName: string;
  isDefault: boolean;
  status: number;
}

/** 租户下账号（主账号排最前，最多 50 条，按用户名/姓名搜） */
export const listPlatformTenantUsers = async (tenantId: number | string, keyword?: string) => {
  return unifiedApi.service('platform').get<{ list: PlatformTenantUser[] }>(`/platform/tenants/${tenantId}/users`, {
    params: keyword ? { keyword } : {},
  });
};

/** 重置租户账号密码，newPassword 留空由系统生成；新密码只在响应里返回一次 */
export const resetPlatformTenantUserPassword = async (tenantId: number | string, userId: number, newPassword?: string) => {
  return unifiedApi.service('platform').post<{ username: string; newPassword: string }>(
    `/platform/tenants/${tenantId}/users/reset-password`,
    { user_id: userId, new_password: newPassword || '' },
  );
};

// ===== 按域名解析租户（登录页主题，公开）=====
export interface TenantByDomain {
  matched: boolean;
  tenantId?: number;
  tenantCode?: string;
  tenantName?: string;
  scope?: 'admin' | 'mall';
  logoUrl?: string;
  loginBgUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  subtitle?: string;
}
/** 按当前域名解析租户（登录页渲染主题用，无需登录） */
export const getTenantByDomain = async (host?: string) => {
  return unifiedApi.service('platform').get<TenantByDomain>('/tenant/by-domain', { params: host ? { host } : {} });
};

/**
 * 获取租户配置
 */
export const getTenantConfigs = async (tenantId: string) => {
  return unifiedApi.service('platform').get(`/tenant/${tenantId}/configs`);
};

/**
 * 更新租户配置
 */
export const updateTenantConfig = async (tenantId: string, configKey: string, configValue: string) => {
  return unifiedApi.service('platform').put(`/tenant/${tenantId}/configs/${configKey}`, { configValue });
};

/**
 * 获取可用功能列表
 */
export const getAvailableFeatures = async () => {
  return unifiedApi.service('platform').get('/features');
};

// ==================== 平台-租户集成接口 ====================

/**
 * 获取租户API凭证
 */
export const getTenantAPICredentials = async (id: string): Promise<APICredentials> => {
  const response = await unifiedApi.service('platform').get(`/admin/tenants/${id}/credentials`);
  return response.data;
};

/**
 * 重新生成租户API凭证
 */
export const regenerateTenantAPICredentials = async (id: string): Promise<APICredentials> => {
  const response = await unifiedApi.service('platform').post(`/admin/tenants/${id}/credentials/regenerate`);
  return response.data;
};

/**
 * 获取租户部署信息
 */
export const getTenantDeployInfo = async (id: string): Promise<DeployInfo> => {
  const response = await unifiedApi.service('platform').get(`/admin/tenants/${id}/deploy`);
  return response.data;
};

/**
 * 切换租户部署类型
 */
export const switchTenantDeployType = async (id: string, data: SwitchDeployTypeRequest) => {
  return unifiedApi.service('platform').post(`/admin/tenants/${id}/deploy/switch`, data);
};

/**
 * 获取租户域名信息
 */
export const getTenantDomainInfo = async (id: string): Promise<DomainInfo> => {
  const response = await unifiedApi.service('platform').get(`/admin/tenants/${id}/domain`);
  return response.data;
};

/**
 * 更新租户域名配置
 */
export const updateTenantDomain = async (id: string, data: UpdateDomainRequest) => {
  return unifiedApi.service('platform').put(`/admin/tenants/${id}/domain`, data);
};

/**
 * 更新租户健康状态
 */
export const updateTenantHealthStatus = async (id: string, status: HealthStatus) => {
  return unifiedApi.service('platform').put(`/admin/tenants/${id}/health`, { status });
};